/**
 * Leave Server and Delete Server (Console v2 §3.1), as Discord's server
 * menu has them: a member leaves after a confirmation; the owner deletes
 * after typing the server's name (and a two-step code when they have
 * two-step: the server asks, `runAction` asks the person). The owner never
 * sees Leave ("Transfer ownership first"), and the official server offers
 * neither: everyone is in it from sign-up, and it can't be deleted.
 */
import type { Snowflake } from '@neosocial/protocol';
import { useState } from 'react';
import { create } from 'zustand';
import { tr, useT } from '../i18n';
import { runAction } from '../mod/actions';
import { updateData, useData } from '../store/data';
import { officialGuildId } from '../store/selectors';
import { clearSelfExit, markSelfExit } from '../store/selfExit';
import { goGuild, goHub } from '../store/ui';
import { Btn } from '../ui/kit';
import { confirmDialog, Dialog, toast, type MenuItem } from '../ui/overlay';
import { serversApi } from './api';

/** Forgets a server at once (the gateway's GUILD_DELETE says the same a moment later). */
function forget(guildId: Snowflake): void {
  updateData((s) => {
    if (!s.guilds[guildId]) return s;
    const guilds = { ...s.guilds };
    delete guilds[guildId];
    const channels = Object.fromEntries(
      Object.entries(s.channels).filter(([, c]) => c.guild_id !== guildId),
    );
    return { ...s, guilds, channels, guildOrder: s.guildOrder.filter((g) => g !== guildId) };
  });
}

/** After leaving or deleting the open server: back to the official one, as Discord goes Home. */
function away(guildId: Snowflake): void {
  forget(guildId);
  const official = officialGuildId(useData.getState());
  if (official) goGuild(official);
  else goHub({ kind: 'home' });
}

/** Leave and Delete for the server menu and the rail's menu; empty for the official server. */
export function serverExitItems(guildId: Snowflake | null): MenuItem[] {
  const d = useData.getState();
  const g = guildId ? d.guilds[guildId] : undefined;
  if (!g || !d.me || officialGuildId(d) === g.id) return [];
  const { t } = tr();
  return g.owner_id === d.me.id
    ? [{ label: t('sv.delete'), icon: 'trash', danger: true, run: () => openDeleteServer(g.id) }]
    : [{ label: t('sv.leave'), icon: 'logout', danger: true, run: () => void leaveServer(g.id) }];
}

export async function leaveServer(guildId: Snowflake): Promise<boolean> {
  const { t } = tr();
  const name = useData.getState().guilds[guildId]?.name ?? '';
  const ok = await confirmDialog({
    title: t('sv.leave.title', { name }),
    body: t('sv.leave.body', { name }),
    confirmLabel: t('sv.leave'),
    danger: true,
  });
  if (!ok) return false;
  // our own GUILD_MEMBER_REMOVE may come before the answer: no "You were removed" for it
  markSelfExit(guildId);
  const r = await runAction(() => serversApi.leave(guildId));
  if (!r.ok) {
    clearSelfExit(guildId);
    return false;
  }
  away(guildId);
  toast(t('sv.leave.done', { name }), { icon: 'logout', tone: 'ok' });
  return true;
}

const useDeleting = create<{ guildId: Snowflake | null }>(() => ({ guildId: null }));

export function openDeleteServer(guildId: Snowflake): void {
  useDeleting.setState({ guildId });
}

/** Hub mounts it once: the Delete Server dialog while one is asked for. */
export function DeleteServerHost() {
  const guildId = useDeleting((s) => s.guildId);
  if (!guildId) return null;
  return <DeleteServerDialog key={guildId} guildId={guildId} />;
}

function DeleteServerDialog({ guildId }: { guildId: Snowflake }) {
  const { t } = useT();
  const name = useData((d) => d.guilds[guildId]?.name ?? '');
  const [typed, setTyped] = useState('');
  const [wrong, setWrong] = useState(false);
  const [busy, setBusy] = useState(false);
  const close = () => useDeleting.setState({ guildId: null });
  const submit = async () => {
    if (busy) return;
    // Discord's check: the name as it is, around spaces aside
    if (typed.trim() !== name.trim()) {
      setWrong(true);
      return;
    }
    setBusy(true);
    markSelfExit(guildId);
    const r = await runAction(() => serversApi.remove(guildId));
    setBusy(false);
    if (!r.ok) {
      clearSelfExit(guildId);
      return;
    }
    close();
    away(guildId);
    toast(t('sv.delete.done', { name }), { icon: 'trash', tone: 'ok' });
  };
  return (
    <Dialog
      title={t('sv.delete.title', { name })}
      icon="trash"
      tone="danger"
      cls="sv-delete-dlg"
      onClose={close}
      actions={
        <>
          <Btn kind="ghost" size="sm" onClick={close}>
            {t('c.cancel')}
          </Btn>
          <Btn kind="danger" size="sm" busy={busy} onClick={() => void submit()}>
            {t('sv.delete')}
          </Btn>
        </>
      }
    >
      <p>{t('sv.delete.body', { name })}</p>
      <form
        className="ns-field"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label>
          <span className="ns-label">{t('sv.delete.type')}</span>
          <input
            className="nx-input"
            dir="auto"
            value={typed}
            data-autofocus
            autoComplete="off"
            aria-invalid={wrong || undefined}
            onChange={(e) => {
              setTyped(e.target.value);
              setWrong(false);
            }}
          />
        </label>
        {wrong ? (
          <p className="ns-err" role="alert">
            {t('sv.delete.mismatch')}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
